import React, { useState, useEffect } from 'react';
import { Activity, Users, Building } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { buildApiUrl } from '../config/api';

interface Invitation {
  id: number;
  email: string;
  role: string;
  organization: {
    id: number;
    name: string;
  };
  inviter: {
    name: string;
    email: string;
  };
  expires_at: string;
}

interface AcceptInvitationProps {
  token: string;
  onAccepted: (token: string, user: any) => void;
}

export default function AcceptInvitation({ token, onAccepted }: AcceptInvitationProps) {
  const [invitation, setInvitation] = useState<Invitation | null>(null);
  const [name, setName] = useState('');
  const [loading, setLoading] = useState(true);
  const [accepting, setAccepting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchInvitation();
  }, [token]);

  const fetchInvitation = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await fetch(buildApiUrl(`api/v1/invitations/${token}`), {
        headers: {
          'Content-Type': 'application/json',
        }
      });
      
      if (!response.ok) {
        if (response.status === 404) {
          throw new Error('Invitation not found or has expired');
        }
        throw new Error('Failed to load invitation');
      }
      
      const data = await response.json();
      setInvitation(data.data.invitation);
      
      // Pre-fill name from email if available
      if (data.data.invitation.email) {
        const emailParts = data.data.invitation.email.split('@');
        if (emailParts.length > 0) {
          setName(emailParts[0].replace(/[._]/g, ' ').replace(/\w\S*/g, (txt: string) => 
            txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase()
          ));
        }
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load invitation');
    } finally {
      setLoading(false);
    }
  };

  const handleAcceptInvitation = async () => {
    if (!name.trim() || !invitation) return;
    
    try {
      setAccepting(true);
      setError(null);
      
      const response = await fetch(buildApiUrl(`api/v1/invitations/${token}/accept`), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: name.trim()
        })
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to accept invitation');
      }
      
      const data = await response.json();
      
      // Call the onAccepted callback with the JWT token and user data
      onAccepted(data.data.token, data.data.user);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to accept invitation');
    } finally {
      setAccepting(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="flex items-center space-x-2">
          <Activity className="h-8 w-8 text-primary animate-pulse" />
          <span className="text-lg">Loading invitation...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <CardTitle className="text-red-600">Invitation Error</CardTitle>
            <CardDescription>{error}</CardDescription>
          </CardHeader>
          <CardContent className="text-center">
            <Button 
              onClick={() => window.location.href = '/'}
              className="mt-4"
            >
              Go to Home
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!invitation) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <CardTitle>Invitation Not Found</CardTitle>
            <CardDescription>This invitation may have expired or been revoked.</CardDescription>
          </CardHeader>
          <CardContent className="text-center">
            <Button 
              onClick={() => window.location.href = '/'}
              className="mt-4"
            >
              Go to Home
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="flex justify-center mb-4">
            <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center">
              <Users className="h-8 w-8 text-primary" />
            </div>
          </div>
          <CardTitle className="text-2xl">You've Been Invited!</CardTitle>
          <CardDescription>
            Join <strong>{invitation.organization.name}</strong> on Turret
          </CardDescription>
        </CardHeader>
        
        <CardContent className="space-y-6">
          <div className="bg-muted/50 p-4 rounded-lg space-y-2">
            <div className="flex items-center space-x-2">
              <Building className="h-4 w-4 text-muted-foreground" />
              <span className="text-sm font-medium">Organization:</span>
              <span className="text-sm">{invitation.organization.name}</span>
            </div>
            <div className="flex items-center space-x-2">
              <Users className="h-4 w-4 text-muted-foreground" />
              <span className="text-sm font-medium">Role:</span>
              <span className="text-sm capitalize">{invitation.role}</span>
            </div>
            <div className="text-sm text-muted-foreground">
              Invited by {invitation.inviter.name} ({invitation.inviter.email})
            </div>
            <div className="text-xs text-muted-foreground">
              Expires on {formatDate(invitation.expires_at)}
            </div>
          </div>
          
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="name">Your Full Name</Label>
              <Input
                id="name"
                type="text"
                placeholder="Enter your full name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                disabled={accepting}
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="email">Email Address</Label>
              <Input
                id="email"
                type="email"
                value={invitation.email}
                disabled
                className="bg-muted"
              />
            </div>
          </div>
          
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3">
              <div className="text-sm text-red-600">{error}</div>
            </div>
          )}
          
          <div className="flex space-x-3">
            <Button 
              variant="outline" 
              onClick={() => window.location.href = '/'}
              disabled={accepting}
              className="flex-1"
            >
              Cancel
            </Button>
            <Button 
              onClick={handleAcceptInvitation}
              disabled={!name.trim() || accepting}
              className="flex-1"
            >
              {accepting ? 'Accepting...' : 'Accept Invitation'}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
} 